import { useCallback, useState } from "react";

import { css } from "@compiled/react";

import HipchatChevronDownIcon from "@atlaskit/icon/glyph/hipchat/chevron-down";

import { useLocalizedHref } from "@/libraries/web-framework";
import { isEqualUnlocalizedPathname } from "@/utils/localization";

import { useLocation } from "../../../utils/location-provider";

export interface PageTreeNodeData {
    key: string;
    name: string;
    urlSlug?: string;
    children?: PageTreeNodeData[];
    level?: number; // allow manually setting the level of indentation
    isRoot?: boolean; // for agile hub, the root nodes have a different style
}

export interface PageTreeProps {
    nodes: PageTreeNodeData[];
}

// eslint-disable-next-line complexity
const PageTreeNode = ({ node }: { node: PageTreeNodeData }) => {
    const { name, urlSlug, children, level, isRoot } = node;

    const location = useLocation();
    const [isSelected, _setIsSelected] = useState(
        urlSlug && isEqualUnlocalizedPathname(location.pathname, urlSlug)
    );

    // deeply find if any child is selected
    const hasSelectedChild = useCallback(
        (currentNode: PageTreeNodeData): boolean => {
            if (
                currentNode.urlSlug &&
                isEqualUnlocalizedPathname(
                    location.pathname,
                    currentNode.urlSlug
                )
            ) {
                return true;
            } else if (currentNode.children) {
                return currentNode.children.some((child) =>
                    hasSelectedChild(child)
                );
            }
            return false;
        },
        [location.pathname]
    );

    const hasDropdown = isRoot && children;

    const [expanded, setExpanded] = useState(hasSelectedChild(node));

    const isRootSelected = isRoot && hasSelectedChild(node);

    const handleClick = () => {
        setExpanded(!expanded);
    };

    const wrapperCss = css({
        marginInlineStart: isRoot ? "0" : `${(level ?? 1) * 15}px`
    });

    const navlinkCss = [
        css({
            fontSize: isRoot ? "1rem" : ".9rem",
            padding: `8px 0 8px 40px`,
            color: isRootSelected ? "#091E42" : "#42526e",
            fontWeight: isRootSelected || isSelected ? "600" : "400",
            textDecoration: "none",
            position: "relative",
            textWrap: "wrap",
            wordBreak: "break-word"
        })
    ];

    const navlinkSpanCss = css({
        position: "relative"
    });

    const navlinkBulletCss = css({
        "&:before": {
            content: "",
            width: "0",
            height: "0",
            insetBlockStart: "0",
            insetBlockEnd: "0",
            margin: "auto",
            border: "2px solid",
            borderRadius: "10px",
            position: "absolute",
            insetInlineStart: "-20px"
        }
    });

    const navlinkIconCss = [
        css({
            position: "absolute",
            insetInlineStart: "0",
            marginInlineStart: "-38px",
            insetBlockStart: "-5.5px",
            transformOrigin: "16px 16px",
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 300ms ease"
        })
    ];

    const navlinkACss = [
        css({
            "margin": "0",
            "&:hover": {
                textDecoration: "underline"
            }
        }),
        isSelected && !isRoot && navlinkBulletCss
    ];

    const dropdownCss = [
        css({
            position: "relative",
            overflowX: "hidden",
            overflowY: "hidden"
        }),
        !expanded &&
            css({
                transition: "max-height 0.5s ease",
                maxHeight: "0px"
            }),
        expanded &&
            css({
                transition: "max-height 2s ease",
                maxHeight: "10000px"
            })
    ];

    return (
        <div css={wrapperCss}>
            {/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div css={navlinkCss} onClick={handleClick} onKeyDown={handleClick}>
                <span css={navlinkSpanCss}>
                    {hasDropdown && (
                        <div
                            aria-label={expanded ? "collapse" : "expand"}
                            className="agile-sidenav-icon"
                            css={navlinkIconCss}
                        >
                            <HipchatChevronDownIcon size="large" label="" />
                        </div>
                    )}
                    <a
                        aria-label={`link to ${name}`}
                        css={navlinkACss}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="micrositeNav"
                        data-product-key="jiraSoftware"
                        href={useLocalizedHref(urlSlug ?? "")}
                        onClick={(e) => {
                            isRoot && hasDropdown ? e.preventDefault() : null;
                        }} // clicking on the root dropdown does not navigate
                    >
                        {name !== undefined && name.length > 0
                            ? name.charAt(0).toUpperCase() + name.slice(1)
                            : name}
                    </a>
                </span>
            </div>
            <div css={dropdownCss}>
                {children && <PageTree nodes={children} />}
            </div>
        </div>
    );
};

export const PageTree = ({ nodes }: PageTreeProps) => {
    return (
        <div>
            {nodes.map((node) => (
                <PageTreeNode key={node.key} node={node} />
            ))}
        </div>
    );
};
